import { initLazyScript } from '../utils/theme-functions';

/**
 * SectionTabNavigator - Tab navigation component for Shopify sections
 *
 * Provides tab-based content filtering with animated background blob indicator.
 * Integrates with Shopify theme editor for block selection support.
 *
 * Required HTML structure:
 * - data-section-id: ID of the section containing tab content
 * - data-active-text-class: CSS class to apply to active tab
 * - [data-navigate-tab]: Navigation trigger elements
 * - [data-tab]: Content elements to show/hide
 * - .background-blob: Optional animated background indicator
 *
 * @fires sectionTabNavigated - Dispatched when tab changes
 */

const SELECTORS = {
    navigateTab: '[data-navigate-tab]',
    tab: '[data-tab]',
    notAll: 'data-not-all',
    backgroundBlob: '.background-blob',
    flickityTab: 'data-flickity-tab'
};

const CSS_CLASSES = {
    hidden: 'js-hidden'
};

const EVENTS = {
    tabNavigated: 'sectionTabNavigated',
    blockSelect: 'shopify:block:select'
};

const ALL_TAB_IDENTIFIERS = ['all', 'alla'];

class SectionTabNavigator extends HTMLElement {
    constructor() {
        super();
        this.target = null;
        this.section = null;
        this.backgroundBlob = null;
        this.activeTextClass = null;
        initLazyScript(this, this.init.bind(this));
    }

    init() {
        const { sectionId, activeTextClass } = this.dataset;

        if (!sectionId) {
            console.error('SectionTabNavigator: Missing required data-section-id attribute');
            return;
        }

        this.section = document.getElementById(sectionId);

        if (!this.section) {
            console.error(`SectionTabNavigator: Section with id "${sectionId}" not found`);
            return;
        }

        this.activeTextClass = activeTextClass;
        this.backgroundBlob = this.querySelector(SELECTORS.backgroundBlob);

        const firstNavigator = this.querySelector(SELECTORS.navigateTab);

        if (!firstNavigator) {
            console.warn("SectionTabNavigator: No navigation elements found", this);
            return;
        }

        this.setupEventListeners();
        this.navigate(firstNavigator);
    }

    setupEventListeners() {
        this.handleClickBound = this.handleClick.bind(this);
        this.addEventListener('click', this.handleClickBound);

        // Set up ARIA attributes
        this.setAttribute('role', 'tablist');

        const navigators = this.querySelectorAll(SELECTORS.navigateTab);
        navigators.forEach(nav => {
            nav.setAttribute('role', 'tab');
            nav.setAttribute('aria-selected', 'false');
            if (!nav.hasAttribute('tabindex')) {
                nav.setAttribute('tabindex', '-1');
            }
        });

        // Shopify theme editor integration
        if (typeof Shopify !== 'undefined' && Shopify.designMode && this.section) {
            this.blockSelectHandlerBound = this.handleBlockSelect.bind(this);
            this.section.addEventListener(EVENTS.blockSelect, this.blockSelectHandlerBound);
        }
    }

    /**
     * Handles click events on navigation elements.
     * @param {Event} evt - The click event object.
     */
    handleClick(evt) {
        const target = evt.target.closest(SELECTORS.navigateTab);

        if (target) {
            evt.preventDefault();
            this.navigate(target);
        }
    }

    /**
     * Navigates to the specified tab.
     * @param {HTMLElement} target - The navigation element clicked.
     */
    navigate(target) {
        try {
            if (!target) {
                throw new Error('Invalid navigation target');
            }

            const previousTarget = this.target;
            this.target = target;

            this.updateUI(target, previousTarget);

            const tabName = target.dataset.navigateTab;

            if (!tabName) {
                throw new Error('Navigation target missing data-navigate-tab attribute');
            }

            this.updateTabVisibility(tabName);
            this.dispatchNavigationEvent(tabName);
        } catch (error) {
            console.error('SectionTabNavigator: Navigation failed', error);
        }
    }

    /**
     * Updates visibility of tab content elements.
     * @param {string} tabName - The name of the tab to show.
     */
    updateTabVisibility(tabName) {
        const tabEls = this.section.querySelectorAll(SELECTORS.tab);

        tabEls.forEach((tabEl) => {
            // Show all tabs if "all" is selected and element doesn't have data-not-all
            if (ALL_TAB_IDENTIFIERS.includes(tabName) && !tabEl.hasAttribute(SELECTORS.notAll)) {
                tabEl.classList.remove(CSS_CLASSES.hidden);
                tabEl.setAttribute('aria-hidden', 'false');
                return;
            }

            // Show/hide based on tab match
            const elTabs = tabEl.dataset.tab.split(',');
            const show = elTabs.includes(tabName);

            tabEl.classList.toggle(CSS_CLASSES.hidden, !show);
            tabEl.setAttribute('aria-hidden', !show);
        });
    }

    /**
     * Dispatches custom event when tab navigation occurs.
     * @param {string} tabName - The name of the navigated tab.
     */
    dispatchNavigationEvent(tabName) {
        this.section.dispatchEvent(
            new CustomEvent(EVENTS.tabNavigated, {
                detail: { tabName },
                bubbles: true
            })
        );
    }

    /**
     * Updates UI state for active tab.
     * @param {HTMLElement} target - The new active navigation element.
     * @param {HTMLElement} previousTarget - The previously active navigation element.
     */
    updateUI(target, previousTarget) {
        // Remove active state from previous target
        if (previousTarget) {
            if (this.activeTextClass) {
                previousTarget.classList.remove(this.activeTextClass);
            }
            previousTarget.setAttribute('aria-selected', 'false');
            previousTarget.setAttribute('tabindex', '-1');
        }

        // Add active state to new target
        if (target) {
            if (this.activeTextClass) {
                target.classList.add(this.activeTextClass);
            }
            target.setAttribute('aria-selected', 'true');
            target.setAttribute('tabindex', '0');

            if (this.backgroundBlob) {
                this.updateBackgroundBlobPosition(target);
            }
        }
    }

    /**
     * Updates the position and size of the background blob indicator.
     * @param {HTMLElement} target - The target navigation element.
     */
    updateBackgroundBlobPosition(target) {
        if (!target.offsetParent) {
            console.warn('SectionTabNavigator: Target has no offset parent for blob positioning');
            return;
        }

        const targetRect = target.getBoundingClientRect();
        const parentRect = target.offsetParent.getBoundingClientRect();

        requestAnimationFrame(() => {
            this.backgroundBlob.style.cssText = `
                width: ${targetRect.width}px;
                left: ${targetRect.left - parentRect.left}px;
            `;
        });
    }

    /**
     * Handles Shopify theme editor block selection.
     * @param {CustomEvent} evt - The block select event.
     */
    handleBlockSelect(evt) {
        const blockEl = evt.target;

        if (!blockEl) {
            console.warn('SectionTabNavigator: No block element found in select event');
            return;
        }

        // If block is hidden, find which tab it belongs to and navigate to it
        if (blockEl.classList.contains(CSS_CLASSES.hidden)) {
            let tabs = [];

            if (blockEl.hasAttribute('data-tab')) {
                tabs = blockEl.dataset.tab.split(',');
            } else if (blockEl.hasAttribute(SELECTORS.flickityTab)) {
                tabs = blockEl.dataset.flickityTab.split(',');
            }

            if (tabs.length > 0) {
                const tabName = tabs[0];
                const navElement = this.querySelector(`${SELECTORS.navigateTab}="${tabName}"]`);

                if (navElement) {
                    this.navigate(navElement);
                } else {
                    console.warn(`SectionTabNavigator: No navigation element found for tab "${tabName}"`);
                }
            }
        }
    }

    disconnectedCallback() {
        // Clean up event listeners
        if (this.handleClickBound) {
            this.removeEventListener('click', this.handleClickBound);
        }

        if (this.blockSelectHandlerBound && this.section) {
            this.section.removeEventListener(EVENTS.blockSelect, this.blockSelectHandlerBound);
        }

        // Clean up references
        this.section = null;
        this.target = null;
        this.backgroundBlob = null;
    }
}

customElements.define('section-tab-navigator', SectionTabNavigator);

/**
 * RadioBlob - Animated blob indicator for radio button groups
 *
 * Provides visual feedback for radio button selection with animated background blob.
 *
 * Required HTML structure:
 * - data-active-text-class: CSS class to apply to active option
 * - input[type="radio"]: Radio button inputs
 * - .background-blob: Animated background indicator
 */
export class RadioBlob extends HTMLElement {
    constructor() {
        super();
        this.target = null;
        this.backgroundBlob = null;
        this.activeTextClass = null;
        initLazyScript(this, this.init.bind(this));
    }

    init() {
        this.activeTextClass = this.dataset.activeTextClass;
        this.backgroundBlob = this.querySelector(SELECTORS.backgroundBlob);

        const firstChecked = this.querySelector('input[type="radio"]:checked');

        if (!firstChecked) {
            console.warn("RadioBlob: No checked radio button found", this);
            return;
        }

        this.setupEventListeners();

        // Initialize with checked radio
        const firstTarget = firstChecked.closest('li');
        if (firstTarget) {
            this.target = firstTarget;
            this.updateUI(firstTarget, null);
        }
    }

    setupEventListeners() {
        this.handleInputBound = this.handleInput.bind(this);
        this.addEventListener('input', this.handleInputBound);
    }

    /**
     * Handles input events from radio buttons.
     * @param {Event} evt - The input event object.
     */
    handleInput(evt) {
        const target = evt.target.closest('li');

        if (target) {
            const previousTarget = this.target;
            this.target = target;
            this.updateUI(target, previousTarget);
        }
    }

    /**
     * Updates UI state for active radio button.
     * @param {HTMLElement} target - The new active list item.
     * @param {HTMLElement} previousTarget - The previously active list item.
     */
    updateUI(target, previousTarget) {
        if (previousTarget && this.activeTextClass) {
            previousTarget.classList.remove(this.activeTextClass);
        }

        if (target && this.backgroundBlob) {
            if (this.activeTextClass) {
                target.classList.add(this.activeTextClass);
            }

            this.updateBackgroundBlobPosition(target);
        }
    }

    /**
     * Updates the position and size of the background blob indicator.
     * @param {HTMLElement} target - The target list item element.
     */
    updateBackgroundBlobPosition(target) {
        if (!target.offsetParent) {
            console.warn('RadioBlob: Target has no offset parent for blob positioning');
            return;
        }

        const targetRect = target.getBoundingClientRect();
        const parentRect = target.offsetParent.getBoundingClientRect();

        requestAnimationFrame(() => {
            this.backgroundBlob.style.cssText = `
                width: ${targetRect.width}px;
                left: ${targetRect.left - parentRect.left}px;
                top: ${targetRect.top - parentRect.top}px;
            `;
        });
    }

    disconnectedCallback() {
        if (this.handleInputBound) {
            this.removeEventListener('input', this.handleInputBound);
        }

        this.backgroundBlob = null;
        this.target = null;
    }
}

customElements.define('radio-blob', RadioBlob);
